import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { InstantTransitionPhase, SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    getResourceTypeRingRadius,
    getToolRingOuterRadius,
    getToolStepRingGap,
} from '@/workflow/_pure/config/derivationConfig';
import { deriveResourceCollections as deriveBaseResourceCollections } from '@/workflow/_pure/derivation/baseDerivation';
import {
    deriveMaximumAdjacentWorkflowStateEntryArity,
    deriveRadialLayoutMetrics,
    validateRadialLayoutConfig,
} from '@/workflow/_pure/derivation/geometry';
import { WORKFLOW_KEYS } from '@/workflow/_pure/keyCodec';
import { requireRunnerRuntimeState } from '@/workflow/modes/_@shared/_pure/runtimeStateGuards';
import { CAMERA_DEFAULTS } from '@/workflow-runner/_data/config/fragments/camera';
import { requireConfig, resolveOperationPresentationMode } from '@/workflow-runner/_pure/config/config';
import { deriveAutoplayCameraGlide } from '@/workflow-runner/_pure/derivation/autoplayCameraGlide';
import { withCaseTori } from '@/workflow-runner/_pure/derivation/caseTori';
import { resolveWorkflowBeatGrouping } from '@/workflow-runner/episodeCatalog';
import { deriveRootLoopStepKeyByCloneLoopStepKey } from '@/workflow-runner/_pure/model/traceHelpers';
import { deriveCaseMembership } from '@/workflow-runner/_pure/derivation/derivation';
import { derivePanelCameraIngress } from '@/workflow-runner/_pure/derivation/panelCameraIngress';
import {
    deriveConstellationDerivations,
    deriveSpaceRenderOptions,
    deriveSyntheticItemCollectionDerivations,
} from '@/workflow-runner/_pure/derivation/mode';
import {
    deriveBranchExitRebindForwardTransitionPhases,
    deriveBranchExitRebindReverseTransitionPhases,
    deriveLoopContinuationForwardTransitionPhases,
    deriveLoopContinuationReverseTransitionPhases,
    deriveResourceTypeRadialActivationWavePhases,
    deriveWorkflowStateEntryLabelRemovals,
} from '@/workflow-runner/_pure/derivation/transitionPhases';
import { buildToolStepItemKey } from '@/workflow-runner/_pure/derivation/transitionRenderState';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import {
    augmentFrameWithPrimitiveOperationRods,
    resolveReadyPrimitiveOperationReplay,
    toPrimitiveCameraPlanKey,
} from '@/workflow-runner/_pure/primitiveOperationRod';
import { selectWorkflowCameraDirections, WORKFLOW_DIRECTION_CATALOG } from '@/workflow-runner/_pure/workflowDirectionRules';
import { deriveWorkflowCameraMoment } from '@/workflow-runner/_pure/workflowMoment';
import {
    derivePostItemTraceContext,
    derivePreItemTraceContext,
} from '@/workflow-runner/model/traceContext';
import { createTraceSnapshotResolver } from '@/workflow-runner/model/traceSnapshot';
import { withCameraGraph } from '@/workflow-runner/cameraGraph';
import { deriveTraceTeaching, withTraceTeachingCamera } from '@/workflow-runner/traceTeaching';
import { operationCaptionModel, withOperationCaption } from '@/workflow-runner/_pure/derivation/operationCaption';
import {
    derivePrimitiveCameraPlanCore,
    derivePrimitiveOperationRodTransitionPhases,
} from '@/workflow-runner/primitiveOperationPlayback';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { Config } from '@/workflow/_contract/config';
import type { WorkflowModeAugmentationInput } from '@/workflow/_contract/modeAugmentation';
import type {
    InteractiveRuntimeState,
    ModeAugmentation,
    PipelineContext,
} from '@/workflow/modes/_@shared/_contract/mode';
import type { WorkflowCameraDirectionEffect } from '@/workflow-runner/_contract/_~generated/cameraDirectionEffect';
import type { PrimitiveCameraPlanCoreDeriver } from '@/workflow-runner/_contract/primitiveCameraPlan';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';

function deriveWorkflowStateEntryGapForSnapshot(args: {
    config: Config;
    orderedToolSteps: Parameters<typeof deriveMaximumAdjacentWorkflowStateEntryArity>[0]['orderedToolSteps'];
    toolMap: Parameters<typeof deriveMaximumAdjacentWorkflowStateEntryArity>[0]['toolMap'];
}): number {
    const resourceTypeRingRadius = getResourceTypeRingRadius(args.config);
    validateRadialLayoutConfig({
        toolRingOuterRadius: getToolRingOuterRadius(args.config),
        resourceTypeRingRadiusMultiplier: args.config.geometryScale.multipliers.resourceTypes.ringRadiusFromRoot,
        toolStepRingGapMultiplier: args.config.geometryScale.multipliers.toolSteps.ringGapFromResourceTypeRing,
    });
    const maxArityBetweenToolSteps = deriveMaximumAdjacentWorkflowStateEntryArity({
        orderedToolSteps: [...args.orderedToolSteps],
        toolMap: args.toolMap,
    });
    return deriveRadialLayoutMetrics({
        resourceTypeRingRadius,
        toolStepRingGap: getToolStepRingGap(args.config),
        toolStepCount: args.orderedToolSteps.length,
        maxArityBetweenToolSteps,
    }).workflowStateEntryGap;
}

function deriveCachedPostItemTraceContext(args: {
    input: WorkflowModeAugmentationInput<InteractiveRuntimeState, PipelineContext>;
    config: Config;
    itemCollections: ItemCollection[];
}) {
    const runnerPipelineContext = args.input.pipelineContext;
    if (!runnerPipelineContext) {
        throw new Error('Runner pipeline context is required for runner mode derivation.');
    }

    return derivePostItemTraceContext({
        cosmosData: args.input.cosmosData,
        runtimeState: requireRunnerRuntimeState(args.input.runtimeState),
        config: args.config,
        itemCollections: args.itemCollections,
        preItemTraceContext: runnerPipelineContext.preItemTraceContext,
        traceSnapshotResolver: runnerPipelineContext.traceSnapshotResolver,
    });
}

/**
 * Memoises the step-independent primitive camera plan: base, settled-target and transition derivations of one
 * arithmetic step otherwise each recompute it. WorkflowStates, config and toolMap are compared by identity.
 */
export function createPrimitiveCameraPlanCoreMemo(): PrimitiveCameraPlanCoreDeriver & { clear: () => void } {
    let byWorkflowState = new WeakMap<object, WeakMap<object, Map<string, ReturnType<PrimitiveCameraPlanCoreDeriver>>>>();
    const absent = {};
    let identity: { config: object; toolMap: object } | undefined;
    const clear = () => {
        byWorkflowState = new WeakMap();
        identity = undefined;
    };
    const derive: PrimitiveCameraPlanCoreDeriver = (args) => {
        const key = toPrimitiveCameraPlanKey(args);
        if (key === undefined) return derivePrimitiveCameraPlanCore(args);
        if (identity?.config !== args.config || identity.toolMap !== args.toolMap) {
            identity = { config: args.config, toolMap: args.toolMap };
            byWorkflowState = new WeakMap();
        }
        let byPost = byWorkflowState.get(args.workflowState);
        if (!byPost) byWorkflowState.set(args.workflowState, byPost = new WeakMap());
        const post = args.postWorkflowState ?? absent;
        let byKey = byPost.get(post);
        if (!byKey) byPost.set(post, byKey = new Map());
        if (!byKey.has(key)) byKey.set(key, derivePrimitiveCameraPlanCore(args));
        return byKey.get(key);
    };
    return Object.assign(derive, { clear });
}

function buildRunnerModeAugmentationController(
    deriveCameraPlanCore: PrimitiveCameraPlanCoreDeriver, directionCatalog: DirectionCatalog<WorkflowCameraDirectionEffect>,
): ModeAugmentation {
    type TransitionArgs = Parameters<NonNullable<ModeAugmentation['deriveTransitionPhases']>>[0];
    let targetCache: {
        config: Config;
        cosmosData: TransitionArgs['input']['cosmosData'];
        spaceKey: TransitionArgs['input']['spaceKey'];
        deriveBaseFrame: TransitionArgs['deriveBaseFrame'];
        runtimeState: RuntimeState;
        traceSnapshot: ReturnType<typeof derivePreItemTraceContext>['traceSnapshot'];
        frame: SpaceDerivationFrame;
    } | undefined;
    const sameValues = (previous: object, next: object): boolean => {
        const keys = Object.keys(next);
        return keys.length === Object.keys(previous).length
            && keys.every((key) => Object.prototype.hasOwnProperty.call(previous, key)
                && Object.is(Reflect.get(previous, key), Reflect.get(next, key)));
    };
    return {
    deriveResourceCollections(args) {
        return deriveBaseResourceCollections({
            cosmosData: args.input.cosmosData,
            config: args.config,
        });
    },

    deriveSyntheticItemCollectionDerivations(args) {
        const preItemTraceContext = args.input.pipelineContext?.preItemTraceContext;
        if (!preItemTraceContext) {
            throw new Error('Runner pre-item trace context is required for runner synthetic item collection derivation.');
        }
        return deriveSyntheticItemCollectionDerivations({ ...args, preItemTraceContext });
    },

    deriveConstellationDerivations(args) {
        const preItemTraceContext = args.input.pipelineContext?.preItemTraceContext;
        if (!preItemTraceContext) {
            throw new Error('Runner pre-item trace context is required for runner constellation derivation.');
        }
        return deriveConstellationDerivations({ ...args, preItemTraceContext });
    },

    deriveSpaceRenderOptions(args) {
        const postItemTraceContext = deriveCachedPostItemTraceContext(args);
        return deriveSpaceRenderOptions({ ...args, postItemTraceContext });
    },

    deriveItemParametricState() {
        // Transition patches remain available for future TransitionPhase production.
        // The base parametric state is now flattened from derivations at the pipeline boundary.
        return undefined;
    },

    augmentBaseFrame(args) {
        const config = requireConfig(args.config);
        const runtimeState = requireRunnerRuntimeState(args.input.runtimeState);
        const pendingReplayMove = runtimeState.pendingReplayMove;
        const pendingPrimitiveOperationMove = pendingReplayMove?.eventKind === 'primitive-operation'
            ? pendingReplayMove
            : undefined;
        const traceSnapshotResolver = args.input.pipelineContext?.traceSnapshotResolver;
        const traceSnapshot = args.input.pipelineContext?.preItemTraceContext.traceSnapshot;
        const sourceRodTraceSnapshot = args.input.pipelineContext?.preItemTraceContext.traceSnapshotView;
        const completedRodTraceSnapshot = pendingReplayMove?.eventKind !== 'primitive-operation'
            && pendingReplayMove && 'followsPrimitiveOperation' in pendingReplayMove && pendingReplayMove.followsPrimitiveOperation
            && traceSnapshotResolver
            ? traceSnapshotResolver.deriveTraceSnapshotAtPosition({
                cosmosData: args.input.cosmosData,
                runtimeState,
                replayPositionIndex: pendingReplayMove.toPositionIndex,
            })
            : undefined;
        // Keep source topology through structural fades, but retain the completed primitive's values.
        const rodTraceSnapshot = sourceRodTraceSnapshot && completedRodTraceSnapshot
            ? {
                ...sourceRodTraceSnapshot,
                position: {
                    ...sourceRodTraceSnapshot.position,
                    workflowState: {
                        ...sourceRodTraceSnapshot.position.workflowState,
                        ...completedRodTraceSnapshot.position.workflowState,
                    },
                },
            }
            : sourceRodTraceSnapshot;
        const workflowStateEntryGap = deriveWorkflowStateEntryGapForSnapshot({
            config,
            orderedToolSteps: rodTraceSnapshot?.position.orderedToolSteps ?? [],
            toolMap: args.input.cosmosData.toolMap,
        });
        const augmented = augmentFrameWithPrimitiveOperationRods({
            frame: args.frame,
            traceSnapshot: rodTraceSnapshot,
            pendingReplayMove: pendingPrimitiveOperationMove,
            pendingTargetSnapshot: pendingPrimitiveOperationMove && traceSnapshotResolver
                ? traceSnapshotResolver.deriveTraceSnapshotAtPosition({
                    cosmosData: args.input.cosmosData,
                    runtimeState,
                    replayPositionIndex: pendingPrimitiveOperationMove.toPositionIndex,
                })
                : undefined,
            config,
            toolMap: args.input.cosmosData.toolMap,
            workflowStateEntryGap,
        });
        const cameraFrame = withCameraGraph({
            frame: withCaseTori({
                frame: augmented,
                config,
                grouping: runtimeState.episode ? resolveWorkflowBeatGrouping(runtimeState) : runtimeState.explorationGrouping,
                macroStepOwnerByKey: (runtimeState.episode || runtimeState.explorationGrouping !== undefined) && sourceRodTraceSnapshot
                    ? deriveRootLoopStepKeyByCloneLoopStepKey({
                        tickTracePoints: sourceRodTraceSnapshot.tickTracePoints,
                        appliedTickCount: sourceRodTraceSnapshot.tickTracePoints.length,
                    }) : undefined,
                memberships: deriveCaseMembership({
                    cosmosData: args.input.cosmosData, config, traceSnapshot: sourceRodTraceSnapshot,
                }),
                traceHandle: sourceRodTraceSnapshot?.selectedTraceHandle ?? '',
            }), config, traceSnapshot: sourceRodTraceSnapshot,
            grouping: resolveWorkflowBeatGrouping(runtimeState),
            activeSnapshot: pendingPrimitiveOperationMove && traceSnapshotResolver
                ? traceSnapshotResolver.deriveTraceSnapshotAtPosition({
                    cosmosData: args.input.cosmosData, runtimeState,
                    replayPositionIndex: pendingPrimitiveOperationMove.toPositionIndex,
                }) : undefined,
            toolMap: args.input.cosmosData.toolMap,
            pendingReplayMove: pendingPrimitiveOperationMove,
            replayComplete: runtimeState.pendingReplayMove === null && traceSnapshot !== undefined
                && traceSnapshot.timeline.replayPositionCount > 0
                && traceSnapshot.position.appliedReplayPositionIndex >= traceSnapshot.timeline.replayPositionCount,
            workflowStateEntryGap,
            deriveCameraPlanCore,
            directionCatalog: config.adapterModeConfig.sceneDirectionCatalogs?.replay ?? directionCatalog,
        });
        return withOperationCaption(withTraceTeachingCamera(cameraFrame, deriveTraceTeaching(cameraFrame, runtimeState,
            args.input.cosmosData, config, sourceRodTraceSnapshot)), config, sourceRodTraceSnapshot, args.input.cosmosData.toolMap);
    },

    deriveGuidance({ frame, input, config }) {
        return deriveTraceTeaching(frame, requireRunnerRuntimeState(input.runtimeState), input.cosmosData,
            requireConfig(config), input.pipelineContext?.preItemTraceContext.traceSnapshotView)?.guidance;
    },

    deriveCameraInitializationKey(args) {
        // Keyed by the resolved trace only, so replay moves never refit; absent until a trace exists.
        const selectedTraceHandle = args.input.pipelineContext?.preItemTraceContext.traceSnapshotView?.selectedTraceHandle;
        return selectedTraceHandle ? `workflow-runner:${selectedTraceHandle}` : undefined;
    },

    deriveOverviewPadding({ config }) {
        return requireConfig(config).adapterModeConfig.camera?.overviewPadding
            ?? CAMERA_DEFAULTS.overviewPadding;
    },

    deriveTransitionPhases(args) {
        const config = requireConfig(args.config);
        const runtimeState = requireRunnerRuntimeState(args.input.runtimeState);
        const pendingReplayMove = runtimeState.pendingReplayMove;
        if (!pendingReplayMove) {
            targetCache = undefined;
            return undefined;
        }
        if (pendingReplayMove.eventKind === 'primitive-operation'
            && pendingReplayMove.arithmeticProgress.pendingStepIndex === null) return undefined;

        const runnerPipelineContext = args.input.pipelineContext;
        if (!runnerPipelineContext) {
            throw new Error('Runner pipeline context is required for runner transition phase derivation.');
        }

        const settledRuntimeState: RuntimeState = {
            ...runtimeState,
            traceSelection: {
                ...runtimeState.traceSelection,
                replayPositionIndex: pendingReplayMove.toPositionIndex,
            },
            pendingReplayMove: null,
        };
        const { traceSelection: previousSelection, ...previousRuntime } = targetCache?.runtimeState ?? settledRuntimeState;
        const { traceSelection: nextSelection, ...nextRuntime } = settledRuntimeState;
        const targetPreItemTraceContext = derivePreItemTraceContext({
            cosmosData: args.input.cosmosData,
            runtimeState: settledRuntimeState,
            config,
            traceSnapshotResolver: runnerPipelineContext.traceSnapshotResolver,
        });
        const canReuseTarget = targetCache !== undefined
            && targetCache.config === config
            && targetCache.cosmosData === args.input.cosmosData
            && targetCache.spaceKey === args.input.spaceKey
            && targetCache.deriveBaseFrame === args.deriveBaseFrame
            && targetCache.traceSnapshot === targetPreItemTraceContext.traceSnapshot
            && sameValues(previousSelection, nextSelection)
            && sameValues(previousRuntime, nextRuntime);
        const deriveTarget = () => {
            return args.deriveBaseFrame({
                ...args.input,
                runtimeState: settledRuntimeState,
                pipelineContext: {
                    traceSnapshotResolver: runnerPipelineContext.traceSnapshotResolver,
                    preItemTraceContext: targetPreItemTraceContext,
                },
            });
        };
        const targetFrame = canReuseTarget && targetCache ? targetCache.frame : deriveTarget();
        // Settlement updates graph metadata, but a configured initial pose is not a navigation request.
        const settledCamera = { ...targetFrame.spaceRenderOptions?.camera };
        delete settledCamera.pose;
        const settledRenderOptions = targetFrame.spaceRenderOptions === undefined ? undefined : {
            ...targetFrame.spaceRenderOptions,
            ...(targetFrame.spaceRenderOptions.camera === undefined ? {} : { camera: settledCamera }),
        };
        const targetActiveToolStep = selectActiveToolStepFromTraceSnapshot(
            targetPreItemTraceContext.activationTraceSnapshotView,
        );
        const targetActiveToolStepItemKey = targetActiveToolStep === undefined
            ? undefined
            : buildToolStepItemKey(targetActiveToolStep.stepKey);
        // Retain only the current settled destination, not every visited replay position.
        targetCache = {
            config, cosmosData: args.input.cosmosData, spaceKey: args.input.spaceKey,
            deriveBaseFrame: args.deriveBaseFrame, runtimeState: settledRuntimeState, frame: targetFrame,
            traceSnapshot: targetPreItemTraceContext.traceSnapshot,
        };

        const phaseBuilderArgs = {
            base: args.base,
            target: targetFrame,
            config,
            preItemTraceContext: runnerPipelineContext.preItemTraceContext,
            activeToolStepItemKey: targetActiveToolStepItemKey,
        };
        const waveTransitionKey = [
            pendingReplayMove.eventKind,
            pendingReplayMove.direction,
            pendingReplayMove.fromPositionIndex,
            pendingReplayMove.toPositionIndex,
        ].join(':');
        const edgeSnapshot = runnerPipelineContext.traceSnapshotResolver.deriveTraceSnapshotAtPosition({
            cosmosData: args.input.cosmosData,
            runtimeState,
            replayPositionIndex: Math.max(pendingReplayMove.fromPositionIndex, pendingReplayMove.toPositionIndex),
        });
        const edgeToolStep = selectActiveToolStepFromTraceSnapshot(edgeSnapshot);
        const cameraDirections = selectWorkflowCameraDirections(
            deriveWorkflowCameraMoment(edgeSnapshot), config.adapterModeConfig.sceneDirectionCatalogs?.replay ?? directionCatalog,
        );
        const deriveWaveStepKey = (frame: SpaceDerivationFrame) => {
            const frameContext = frame === args.base
                ? runnerPipelineContext.preItemTraceContext
                : targetPreItemTraceContext;
            const runtimeToolStepKey = edgeSnapshot?.tickTracePoints.filter((tick) => (
                tick.tickKind === 'tool_execution' && tick.stepKind === 'tool'
            ))[Math.max(pendingReplayMove.fromPositionIndex, pendingReplayMove.toPositionIndex) - 1]?.stepKey as `TOOL_STEP-${string}` | undefined;
            return runtimeToolStepKey === undefined ? edgeToolStep?.stepKey
                : frameContext.traceSnapshot?.macroStepControl.branch.replay.runtimeToBranchToolStepKey.get(runtimeToolStepKey)
                    ?? runtimeToolStepKey;
        };
        const deriveWave = (
            frame: SpaceDerivationFrame,
            stage?: 'input-hold' | 'output-materialize' | 'output-clear',
        ) => {
            // Structural replay can rename branch steps; resolve the causing runtime tick in the
            // frame where the pulse actually plays (before movement / after restoration).
            const stepKey = deriveWaveStepKey(frame);
            return deriveResourceTypeRadialActivationWavePhases({
                ...phaseBuilderArgs,
                base: frame,
                target: pendingReplayMove.eventKind === 'tool-execution' || stage === 'output-materialize' ? targetFrame : frame,
                geometryFrame: frame,
                activeToolStepItemKey: stepKey === undefined ? undefined : buildToolStepItemKey(stepKey),
                pendingReplayMove,
                transitionKey: waveTransitionKey,
                captionModel: config.adapterModeConfig.showOperationCaption
                    ? operationCaptionModel(edgeSnapshot, args.input.cosmosData.toolMap, edgeToolStep?.stepKey, stepKey) : undefined,
                ...(stage === undefined ? {} : { stage }),
            });
        };
        const derivePanelIngressPhase = (frame: SpaceDerivationFrame): TransitionPhase | undefined => {
            const runnerConfig = requireConfig(config);
            const cameraSettings = runnerConfig.adapterModeConfig.camera ?? CAMERA_DEFAULTS;
            if (cameraSettings.navigationMode !== 'guided'
                || resolveOperationPresentationMode(runnerConfig) !== 'panels' || !cameraDirections.ingress) return undefined;
            const stepKey = deriveWaveStepKey(frame);
            if (stepKey === undefined) throw new Error('Guided panel ingress requires a causing ToolStep.');
            const frameContext = frame === args.base
                ? runnerPipelineContext.preItemTraceContext
                : targetPreItemTraceContext;
            const step = frameContext.traceSnapshot?.position.orderedToolSteps.find((entry) => entry.stepKey === stepKey);
            const tool = step && args.input.cosmosData.toolMap.get(step.toolHandle);
            return derivePanelCameraIngress({
                frame, stepKey, direction: pendingReplayMove.direction, transitionKey: waveTransitionKey,
                inputType: tool && Object.values(tool.roleSpec.inputRoleByName)[0]?.resourceTypeHandle,
                outputType: tool && Object.values(tool.roleSpec.outputRoleByName)[0]?.resourceTypeHandle,
                policy: cameraDirections.ingress,
            });
        };
        if (pendingReplayMove.eventKind === 'tool-execution') {
            const waveFrame = pendingReplayMove.direction === 'forward' ? args.base : targetFrame;
            let wave = deriveWave(waveFrame);
            const runnerConfig = requireConfig(config);
            const stepKey = deriveWaveStepKey(waveFrame);
            const step = runnerPipelineContext.preItemTraceContext.traceSnapshot?.position.orderedToolSteps.find((entry) => entry.stepKey === stepKey);
            const tool = step && args.input.cosmosData.toolMap.get(step.toolHandle);
            const types = tool && [...Object.values(tool.roleSpec.inputRoleByName), ...Object.values(tool.roleSpec.outputRoleByName)]
                .map((role) => role.resourceTypeHandle);
            if (pendingReplayMove.direction === 'forward' && runtimeState.playbackAdvanceMode === 'automatic' && runtimeState.isPlaying
                && runnerConfig.adapterModeConfig.camera?.navigationMode === 'guided'
                && resolveOperationPresentationMode(runnerConfig) === 'panels'
                && stepKey && types?.length && types.every((type) => type === types[0]) && cameraDirections.glide) {
                wave = deriveAutoplayCameraGlide({
                    frame: waveFrame, phases: wave, stepKey, resourceType: types[0], policy: cameraDirections.glide,
                });
            }
            const ingress = derivePanelIngressPhase(waveFrame);
            const settle: TransitionPhase = {
                    transitionOptions: { transitionKind: 'instant', transitionKey: `${waveTransitionKey}:settle` },
                    patch: {
                        itemCollections: targetFrame.itemCollections,
                        constellations: targetFrame.constellations,
                        itemParametricState: targetFrame.itemParametricState,
                        constellationParametricState: targetFrame.constellationParametricState,
                        spaceRenderOptions: settledRenderOptions,
                        entityItemLabelChannelsToRemoveByKey: deriveWorkflowStateEntryLabelRemovals({
                            base: args.base, target: targetFrame,
                        }),
                    },
            };
            if (pendingReplayMove.direction === 'reverse') return [settle, ...(ingress ? [ingress] : []), ...wave];
            const clear = wave.at(-1);
            if (clear === undefined) return [settle];
            return [...(ingress ? [ingress] : []), ...wave.slice(0, -1), clear, settle];
        }
        const withStructuralWave = (
            phases: TransitionPhase[] | undefined,
        ): TransitionPhase[] | undefined => {
            if (phases === undefined
                || ('followsPrimitiveOperation' in pendingReplayMove && pendingReplayMove.followsPrimitiveOperation)) {
                return phases;
            }
            const ingress = derivePanelIngressPhase(pendingReplayMove.direction === 'forward' ? args.base : targetFrame);
            const inputWave = pendingReplayMove.direction === 'forward' ? deriveWave(args.base, 'input-hold') : [];
            const isInstant = (phase: TransitionPhase): phase is InstantTransitionPhase => phase.transitionOptions.transitionKind === 'instant';
            const executingToolItemKey = inputWave.flatMap((phase) => isInstant(phase)
                ? phase.patch.spaceRenderOptions?.activation?.activeItemKeyByConstellationKey?.[WORKFLOW_KEYS.constellations.tool] ?? []
                : [])[0];
            const heldStructuralPhases = executingToolItemKey === undefined ? phases : phases.map((phase) => {
                if (!isInstant(phase) || phase.patch.spaceRenderOptions?.activation === undefined) return phase;
                const renderOptions = phase.patch.spaceRenderOptions;
                return {
                    ...phase,
                    patch: {
                        ...phase.patch,
                        spaceRenderOptions: {
                            ...renderOptions,
                            activation: {
                                ...renderOptions.activation,
                                activeItemKeyByConstellationKey: {
                                    ...renderOptions.activation?.activeItemKeyByConstellationKey,
                                    [WORKFLOW_KEYS.constellations.tool]: executingToolItemKey,
                                },
                            },
                        },
                    },
                };
            });
            return pendingReplayMove.direction === 'forward' ? [
                ...(ingress ? [ingress] : []),
                ...inputWave,
                ...deriveWave(args.base, 'output-materialize'),
                ...heldStructuralPhases,
                ...deriveWave(targetFrame, 'output-clear'),
                {
                    transitionOptions: { transitionKind: 'instant', transitionKey: `${waveTransitionKey}:settle-render-options` },
                    patch: { spaceRenderOptions: settledRenderOptions },
                },
            ] : [...phases, ...(ingress ? [ingress] : []), ...deriveWave(targetFrame)];
        };

        if (pendingReplayMove.eventKind === 'primitive-operation') {
            const fromSnapshot = runnerPipelineContext.traceSnapshotResolver.deriveTraceSnapshotAtPosition({
                cosmosData: args.input.cosmosData,
                runtimeState,
                replayPositionIndex: pendingReplayMove.fromPositionIndex,
            });
            const toSnapshot = runnerPipelineContext.traceSnapshotResolver.deriveTraceSnapshotAtPosition({
                cosmosData: args.input.cosmosData,
                runtimeState,
                replayPositionIndex: pendingReplayMove.toPositionIndex,
            });
            const operationPhases = derivePrimitiveOperationRodTransitionPhases({
                base: args.base,
                target: targetFrame,
                fromSnapshot,
                toSnapshot,
                pendingReplayMove,
                config,
                toolMap: args.input.cosmosData.toolMap,
                workflowStateEntryGap: deriveWorkflowStateEntryGapForSnapshot({
                    config,
                    orderedToolSteps: fromSnapshot?.position.orderedToolSteps ?? [],
                    toolMap: args.input.cosmosData.toolMap,
                }),
                deriveCameraPlanCore,
            });
            const replay = resolveReadyPrimitiveOperationReplay({
                fromSnapshot,
                toSnapshot,
                toolMap: args.input.cosmosData.toolMap,
            });
            if (!replay || pendingReplayMove.arithmeticProgress.pendingStepIndex !== replay.resolution.steps.length - 1) {
                return operationPhases;
            }
            const ingress = derivePanelIngressPhase(args.base);
            return [
                ...operationPhases,
                ...(ingress ? [ingress] : []),
                ...deriveResourceTypeRadialActivationWavePhases({
                    base: args.base,
                    target: targetFrame,
                    pendingReplayMove,
                    config,
                    preItemTraceContext: runnerPipelineContext.preItemTraceContext,
                    activeToolStepItemKey: targetActiveToolStepItemKey,
                    transitionKey: `primitive-operation:${pendingReplayMove.fromPositionIndex}:${pendingReplayMove.toPositionIndex}`,
                    captionModel: config.adapterModeConfig.showOperationCaption
                        ? operationCaptionModel(edgeSnapshot, args.input.cosmosData.toolMap) : undefined,
                }),
            ];
        }

        if (pendingReplayMove.eventKind === 'loop_continuation' && pendingReplayMove.direction === 'forward') {
            return withStructuralWave(deriveLoopContinuationForwardTransitionPhases({
                ...phaseBuilderArgs,
                pendingReplayMove: pendingReplayMove as typeof pendingReplayMove & {
                    eventKind: 'loop_continuation'; direction: 'forward';
                },
            }));
        }

        if (pendingReplayMove.eventKind === 'loop_continuation') {
            return withStructuralWave(deriveLoopContinuationReverseTransitionPhases({
                ...phaseBuilderArgs,
                pendingReplayMove: pendingReplayMove as typeof pendingReplayMove & {
                    eventKind: 'loop_continuation'; direction: 'reverse';
                },
            }));
        }

        if (pendingReplayMove.direction === 'forward') {
            return withStructuralWave(deriveBranchExitRebindForwardTransitionPhases({
                ...phaseBuilderArgs,
                pendingReplayMove: pendingReplayMove as typeof pendingReplayMove & {
                    eventKind: 'branch_exit_rebind'; direction: 'forward';
                },
            }));
        }

        return withStructuralWave(deriveBranchExitRebindReverseTransitionPhases({
            ...phaseBuilderArgs,
            pendingReplayMove: pendingReplayMove as typeof pendingReplayMove & {
                eventKind: 'branch_exit_rebind'; direction: 'reverse';
            },
        }));
        },
    };
}

export function createModeAugmentationController(
    directionCatalog: DirectionCatalog<WorkflowCameraDirectionEffect> = WORKFLOW_DIRECTION_CATALOG,
): ModeAugmentation {
    // Rendered item identities must survive rederivation, including animation settlement.
    const traceSnapshotResolver = createTraceSnapshotResolver();
    const deriveCameraPlanCore = createPrimitiveCameraPlanCoreMemo();
    let selectedTrace: object | undefined;
    return {
        ...buildRunnerModeAugmentationController(deriveCameraPlanCore, directionCatalog),
        derivePipelineContext(args) {
            const runtimeState = requireRunnerRuntimeState(args.input.runtimeState);
            const preItemTraceContext = derivePreItemTraceContext({
                cosmosData: args.input.cosmosData,
                runtimeState,
                config: args.config,
                traceSnapshotResolver,
                pendingMoveSnapshotRole: runtimeState.pendingReplayMove ? 'from' : undefined,
            });
            const trace = preItemTraceContext.traceSnapshot?.mutableWorkflowTrace;
            if (selectedTrace !== trace) {
                selectedTrace = trace;
                deriveCameraPlanCore.clear();
            }
            return {
                traceSnapshotResolver,
                preItemTraceContext,
            };
        },
    };
}
